import React, { useEffect, useMemo, useState } from 'react';

import Head from 'next/head';
import { useRouter } from 'next/router';

import { GetStaticPaths, GetStaticProps } from 'next';

import * as Sentry from '@sentry/nextjs';
import clsx from 'clsx';
import format from 'date-fns/format';
import Button from 'epn-ui/umd/dist/components/Button';
import Grid from 'epn-ui/umd/dist/components/Grid';
import Loader from 'epn-ui/umd/dist/components/Loader';
import notification from 'epn-ui/umd/dist/components/Notification';
import debounce from 'lodash/debounce';

import useI18n, { getLocalePaths } from '@i18n';

// Components
import AddPlacementSentModal from '@pages/offers/Modals/AddPlacementSentModal';
import BanReasonModal from '@pages/offers/Modals/BanReasonModal';
import ConnectionModal from '@pages/offers/Modals/ConnectionModal';
import NeedToAddPlacementModal from '@pages/offers/Modals/NeedToAddPlacement';
import Offer, { IOfferDataProp } from '@pages/offers/Offer';
import { IUniqPlacementOption, OffersHeader } from '@pages/offers/OffersHeader';
import ScrollButton from '@pages/offers/ScrollButton';
import SeoContent from '@pages/offers/SeoContent';

// Utils
import { downloadFile } from '@utils/downloadFile';
import sendGoal from '@utils/metrics/ymSendGoal';
import removeEmptyParams from 'src/utils/formatters/removeEmptyParams';
import { AMPLITUDE_EVENTS_KEYS, logAmplitudeEvent } from 'src/utils/metrics/amplitude';
import sendGaEvent from 'src/utils/metrics/ga';

// Helpers
import prepareFilters from '@components/pages/offers/helpers';

// Api
import api from '@api';
import {
    IGetOffersCompilationsDataFormatted,
    IGetOffersDataFormatted,
    IGetPlacementsDataFormatted,
    IGetUserProfileDataFormatted,
} from '@api/types';
import apiHooks, { ICreateUseRequestInfiniteResponse } from '@apiHooks';
import { fetchCompilations, fetchOffers } from '@components/pages/offers/getStaticPropsApi';

import {
    DEFAULT_FILTERS,
    DEFAULT_UNIQ_PLACEMENTS_PARAMS,
    FILTERS_CONVERSION_FUNCS,
    OFFERS_COMPILATIONS_PARAMS,
    OFFERS_PARAMS,
} from '@pages/offers/config';

// HOCs
import Filters from '@components/pages/offers/Filters';
import WithRole, { rolesEnum } from '@HOCs/WithRole';
import { getFiltersFromQuery, transformFiltersToUrl } from '@hooks/useFilters';
import useRefreshToken from '@hooks/useRefreshToken';
import { IFilters } from '@pages/offers/interfaces';

const { useOffersCompilations, useOffers, usePlacementsUniqOffers, useUserProfile } = apiHooks;

export interface IOffersPage {
    preloadedCompilations: IGetOffersCompilationsDataFormatted[];
    preloadedOffers: IGetOffersDataFormatted[];
}

interface IUnitPlacementParams {
    statuses: string;
    fields: string;
    limit: number;
    hash?: string;
}

const OffersPage: React.FC<IOffersPage> = ({ preloadedCompilations, preloadedOffers }) => {
    const { t, locale } = useI18n('Offers');
    const { t: tCommon } = useI18n('Common');
    const filterOptions = { conversionFuncs: FILTERS_CONVERSION_FUNCS, exclude: ['search'] };
    // const [filters, setFilters] = useFilters(DEFAULT_FILTERS, filterOptions);
    const [filters, setFilters] = useState<IFilters>(DEFAULT_FILTERS);
    const isAuth = useRefreshToken();
    const [isClient, setClient] = useState(false);
    const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
    const [isBanReasonModal, setIsBanReasonModal] = useState(false);
    const [isPlacementsEmptyModal, setIsPlacementsEmptyModal] = useState(false);
    const [isAddPlacementSentModal, setIsAddPlacementSentModal] = useState(false);
    const [filtersIsVisible, setFiltersIsVisible] = useState<boolean>(false);
    const [currentPlacement, setCurrentPlacement] = useState<IGetPlacementsDataFormatted>(null);
    const [currentOfferForConnect, setCurrentOfferToConnect] = useState<IOfferDataProp>(null);
    const [uniqPlacementParams, setUniqPlacementParams] = useState<IUnitPlacementParams>(
        DEFAULT_UNIQ_PLACEMENTS_PARAMS,
    );
    const router = useRouter();
    const { pathname, query } = router;

    useEffect(() => {
        setClient(true);
        const filtersFromQuery = getFiltersFromQuery(filterOptions.conversionFuncs);
        setFilters(prev => ({ ...prev, ...filtersFromQuery }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isAuth) {
            sendGoal('view_offers', 89120816);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAuth]);

    const { data: userData } = useUserProfile<IGetUserProfileDataFormatted>({
        condition: !!isAuth,
    });

    const { data: placementsUniqData, mutate } = usePlacementsUniqOffers<
        IGetPlacementsDataFormatted[]
    >({
        params: uniqPlacementParams,
        condition: !!userData?.wmOnboarding?.hasPlacement,
        dependencies: [uniqPlacementParams.statuses, locale, userData?.wmOnboarding?.hasPlacement],
    });

    useEffect(() => {
        if (placementsUniqData?.length && currentPlacement === null) {
            if (query.placement) {
                setCurrentPlacement(
                    placementsUniqData.find(placement => placement.id === query.placement),
                );
            } else {
                setCurrentPlacement(placementsUniqData?.[0]);
                localStorage.removeItem('offersPlacementId');
            }
        }
        if (placementsUniqData?.length && currentPlacement !== null) {
            setCurrentPlacement(prev =>
                placementsUniqData.find(placement => placement.id === prev.id),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [placementsUniqData]);

    const {
        data: offersCompilationsData = preloadedCompilations,
        isLoading: isLoadingCategories,
    } = useOffersCompilations<IGetOffersCompilationsDataFormatted[]>({
        params: {
            ...OFFERS_COMPILATIONS_PARAMS,
            lang: locale,
        },
        dependencies: [locale],
        optionalAuth: true,
    });

    const filtersDependencies = useMemo(
        () =>
            Object.keys(filters).map(key => {
                const value = filters[key];

                if (
                    key === 'allowedCountries' ||
                    key === 'trafficSources' ||
                    key === 'compilationIds' ||
                    key === 'categoriesIds'
                ) {
                    return value === null ? [] : value?.toString();
                }
                if (key === 'placement') {
                    setCurrentPlacement(
                        placementsUniqData.find(placement => placement.id === value) || null,
                    );
                }
                if (key === 'labelIds') {
                    return +value;
                }

                return value;
            }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters],
    );

    const clearAllFilters = () => {
        setFilters(DEFAULT_FILTERS);
        router.push(
            { pathname, search: `` },
            `${pathname.replace('[locale]', query.locale as string)}`,
            { shallow: true },
        );
    };

    const {
        data: offersData = preloadedOffers,
        isLoading: isLoadingOffers,
        isReachingEnd,
        nextPage,
        meta,
        mutate: mutateOffers,
    } = useOffers({
        params: {
            ...OFFERS_PARAMS,
            lang: locale,
            ...prepareFilters(filters, offersCompilationsData, currentPlacement),
        },
        dependencies: [...filtersDependencies, locale],
        condition: !isLoadingCategories,
        options: { persistSize: false },
        optionalAuth: true,
    }) as ICreateUseRequestInfiniteResponse<IGetOffersDataFormatted[]>;

    const placementUniqOptions: IUniqPlacementOption[] = useMemo(
        () =>
            placementsUniqData?.map(placement => ({
                value: placement.id,
                label: placement.url,
                info: placement,
            })) || [],
        [placementsUniqData],
    );

    const preparedOffers = useMemo((): IOfferDataProp[] => {
        if (currentPlacement !== null) {
            return offersData.map(
                (offer: IGetOffersDataFormatted): IOfferDataProp => {
                    const connection = currentPlacement?.connections?.find(
                        placement => +placement.offerId === +offer.id,
                    );
                    const connectionToOfferStatus = connection?.status ? connection.status : null;
                    return { ...offer, status: connectionToOfferStatus } as IOfferDataProp;
                },
            );
        }

        return offersData.map(
            (offer: IGetOffersDataFormatted): IOfferDataProp =>
                ({ ...offer, status: null } as IOfferDataProp),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offersData, currentPlacement, uniqPlacementParams, placementsUniqData]);

    const onExportOffers = (exportFormat: string) => {
        const validFilters = prepareFilters(filters, offersCompilationsData);

        const pure_params = {
            ...validFilters,
            fileFormat: exportFormat,
            lang: locale,
            viewRules: 'role_user',
        };
        const params = removeEmptyParams(pure_params);

        try {
            (api.offer.exportOffers({
                params,
            }) as Promise<Response>)
                .then(res => res.blob())
                .then(blob =>
                    downloadFile(`offers${format(new Date(), 'yyyy-MM-dd')}.${exportFormat}`, blob),
                );
        } catch (error) {
            Sentry.captureException(error);
            console.error(error);
            notification.error({ message: t('Error while exporting. Try again later') });
        }
    };

    const filtersOnChange = (name, values) => {
        try {
            let resultValue = null;
            let isValueSet = false;

            sendGaEvent({
                eventCategory: 'offer',
                eventAction: 'filter',
                eventLabel: name,
            });

            if (Array.isArray(values) && values.length > 0) {
                if (name === 'allowedCountries') {
                    logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.offersFilterByGeo, {
                        geo_id: values.map(item => item.value).join(','),
                    });
                }

                if (name === 'compilationIds') {
                    resultValue = values;
                    isValueSet = true;
                    logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.offersFilterByCategory, {
                        category_id: values.map(item => item).join(','),
                    });
                }
            }

            if (name === 'order') {
                resultValue = values;
                isValueSet = true;
            }

            if (name === 'myPrograms') {
                const newParams: IUnitPlacementParams = {
                    fields: uniqPlacementParams.fields,
                    limit: uniqPlacementParams.limit,
                    statuses: values ? 'allow' : 'review,waiting,allow,ban,recheck',
                };
                if (values) {
                    newParams.hash = currentPlacement.hash;
                }
                setUniqPlacementParams(newParams);
            }
            // if (name === 'trafficSources') {
            //
            // }
            // if (name === 'categoriesIds') {
            //
            // }
            // if (name === 'hasBonusProgram') {
            //
            // }
            // if (name === 'suitablePlacements') {
            //
            // }
            // if (name === 'labelIds') {
            //
            // }
            // if (name === 'hasFeed') {
            // }

            if (!isValueSet) {
                resultValue = values;
            }

            setFilters((prev: IFilters) => {
                const newFilters = { ...prev, [name]: resultValue };
                const clearedFilters = removeEmptyParams(newFilters);
                const urlParams = transformFiltersToUrl(clearedFilters);
                router.push(
                    { pathname, search: `?${urlParams}` },
                    `${pathname.replace('[locale]', query.locale as string)}?${urlParams}`,
                    { shallow: true },
                );

                return newFilters;
            });
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    // useEffect(() => {
    //     const clearedFilters = removeEmptyParams(filters);
    //     const urlParams = transformFiltersToUrl(clearedFilters);
    //     console.log(clearedFilters, urlParams);
    //     // if (values) {
    //     //     router.push(
    //     //         { pathname, search: `?hasFeed=${values}` },
    //     //         `${pathname.replace('[locale]', query.locale as string)}?hasFeed=${values}`,
    //     //         { shallow: true },
    //     //     );
    //     // }
    // }, [filters]);

    const searchOnChange = value => {
        try {
            if (value.length !== 1) {
                setFilters({ ...filters, search: value });
                logAmplitudeEvent(AMPLITUDE_EVENTS_KEYS.offersSearch);
            }
        } catch (e) {
            Sentry.captureException(e);
        }
    };

    const searchOnChangeDebounced = debounce(searchOnChange, 500);

    const toggleFilters = () => {
        setFiltersIsVisible(!filtersIsVisible);
    };

    const isNotFetchingAndEmpty = useMemo(() => !isLoadingOffers && offersData.length === 0, [
        isLoadingOffers,
        offersData,
    ]);

    const isLoadMoreToShow = useMemo(
        () => !isLoadingOffers && !isReachingEnd && offersData.length !== 0,
        [isLoadingOffers, isReachingEnd, offersData],
    );

    const toggleConnectionToOffer = (offerInfo: IOfferDataProp) => {
        setCurrentOfferToConnect(offerInfo);
        setIsConnectModalOpen(true);
    };

    const toggleBanReason = (offerInfo: IOfferDataProp) => {
        setCurrentOfferToConnect(offerInfo);
        setIsBanReasonModal(true);
    };
    const getParticularConnection = (offerId: number) => {
        if (currentPlacement && currentPlacement?.connections) {
            return (
                currentPlacement?.connections?.find(placement => +placement.offerId === +offerId) ||
                null
            );
        }
        return null;
    };

    const getConnectionComment = (offerId: number) => {
        const foundedPlacementConnection = getParticularConnection(offerId);
        return foundedPlacementConnection !== null ? foundedPlacementConnection?.comment : '';
    };

    const toggleIsPlacementsEmpty = () => {
        setIsPlacementsEmptyModal(true);
    };

    return (
        <>
            <Head>
                <title>{t('Affiliate Programs Title', { year: new Date().getFullYear() })}</title>
                <meta
                    name="description"
                    content={t('The best affiliate programs for making money online')}
                />
            </Head>
            <OffersHeader
                offersCount={+meta?.count || 0}
                onChange={filtersOnChange}
                isNotFetchingAndEmpty={isNotFetchingAndEmpty}
                onExportOffers={onExportOffers}
                toggleFilters={toggleFilters}
                filtersIsVisible={filtersIsVisible}
                uniqPlacementOptions={placementUniqOptions}
                values={{
                    order: filters.order,
                    placement: currentPlacement,
                    search: filters.search,
                }}
                isAuth={isAuth}
                onSearch={searchOnChangeDebounced}
            />
            <Grid
                gutter={[
                    { xs: 8, sm: 16, md: 16, lg: 24, xl: 24 },
                    { xs: 16, sm: 16, md: 16, lg: 24, xl: 24 },
                ]}
            >
                <Grid.Col
                    className={clsx({
                        'md:block': true,
                        block: filtersIsVisible,
                        hidden: !filtersIsVisible,
                    })}
                    span={24}
                    xs={24}
                    sm={24}
                    md={6}
                    lg={5}
                    xl={4}
                >
                    <Filters
                        values={filters}
                        onChange={filtersOnChange}
                        onSearch={searchOnChangeDebounced}
                        offersCompilationsData={offersCompilationsData}
                        clearAllFilters={clearAllFilters}
                        isAuth={isAuth}
                    />
                </Grid.Col>
                <Grid.Col span={24} xs={24} sm={24} md={18} lg={19} xl={20}>
                    <Loader loading={isLoadingOffers}>
                        <Grid
                            gutter={[
                                { xs: 16, sm: 16, md: 24, lg: 24, xl: 24 },
                                { xs: 16, sm: 16, md: 24, lg: 24, xl: 24 },
                            ]}
                        >
                            {(isClient ? preparedOffers : preloadedOffers)?.map(offer => (
                                <Grid.Col
                                    span={24}
                                    xs={24}
                                    sm={12}
                                    md={12}
                                    lg={8}
                                    xl={6}
                                    key={`${offer.id}Column`}
                                >
                                    <Offer
                                        key={offer.id}
                                        offer={offer as IOfferDataProp}
                                        onConnect={() => {
                                            if (placementsUniqData?.length) {
                                                toggleConnectionToOffer(offer as IOfferDataProp);
                                            }
                                            toggleIsPlacementsEmpty();
                                        }}
                                        showTheReason={() =>
                                            toggleBanReason(offer as IOfferDataProp)
                                        }
                                        connection={getParticularConnection(offer.id)}
                                        mutateOffers={mutateOffers}
                                        isAuth={isAuth}
                                    />
                                </Grid.Col>
                            ))}
                        </Grid>
                    </Loader>
                    {isLoadingOffers ||
                        (!isLoadingOffers && isLoadMoreToShow && (
                            <div className="flex justify-center h-12 mt-5 mb-16">
                                {isLoadingOffers && <Loader />}

                                {!isLoadingOffers && isLoadMoreToShow && (
                                    <Button size="middle" type="primary" onClick={() => nextPage()}>
                                        {tCommon('Load more')}
                                    </Button>
                                )}
                            </div>
                        ))}
                    <ScrollButton />
                    {isNotFetchingAndEmpty && (
                        <div className="flex justify-center mb-16 rounded-[6px] text-blueGray-800 bg-blueGray-100 p-[32px] text-[14px] xs:text-[16px]">
                            {t('No offers found')}
                        </div>
                    )}
                    <SeoContent />
                </Grid.Col>
            </Grid>
            {isConnectModalOpen && currentOfferForConnect && (
                <ConnectionModal
                    isOpen={isConnectModalOpen}
                    onClose={() => {
                        setIsConnectModalOpen(false);
                        setCurrentOfferToConnect(null);
                    }}
                    offerName={
                        currentOfferForConnect?.title || currentOfferForConnect?.name || null
                    }
                    offerId={currentOfferForConnect?.id}
                    placements={placementUniqOptions}
                    onConnectionSuccess={() => {
                        mutate();
                        setIsConnectModalOpen(false);
                        setIsAddPlacementSentModal(true);
                        setCurrentOfferToConnect(null);
                    }}
                    currentPlacementValue={currentPlacement.id}
                />
            )}
            {isBanReasonModal && currentOfferForConnect && (
                <BanReasonModal
                    isOpen={isBanReasonModal}
                    reason={getConnectionComment(currentOfferForConnect.id)}
                    onClose={() => {
                        setIsBanReasonModal(false);
                        setCurrentOfferToConnect(null);
                    }}
                />
            )}
            {isPlacementsEmptyModal && !placementsUniqData?.length && (
                <NeedToAddPlacementModal
                    isOpen={isPlacementsEmptyModal}
                    onClose={() => setIsPlacementsEmptyModal(false)}
                />
            )}
            {isAddPlacementSentModal && (
                <AddPlacementSentModal
                    isOpen={isAddPlacementSentModal}
                    onClose={() => setIsAddPlacementSentModal(false)}
                />
            )}
        </>
    );
};

export default WithRole(OffersPage, [rolesEnum.guest]);

export const getStaticProps: GetStaticProps = async context => {
    const { locale } = context.params as { locale: string };
    try {
        const compilations: IGetOffersCompilationsDataFormatted[] = await fetchCompilations(locale);

        const offers = await fetchOffers(locale, compilations);

        return {
            props: {
                preloadedCompilations: compilations,
                preloadedOffers: offers,
            },
        };
    } catch (err) {
        console.error(err);
        return {
            props: {
                preloadedCompilations: [],
                preloadedOffers: [],
            },
        };
    }
};

export const getStaticPaths: GetStaticPaths = async () => {
    return { paths: getLocalePaths(), fallback: false };
};
